import React from "react";
import { useSearchParams } from "react-router-dom";
import * as _ from "underscore";
import Accordion from "web-ui/components/internal/Accordion";
import Button from "web-ui/components/internal/Button";
import Checkbox from "web-ui/components/internal/Checkbox";
import Icon, { iconFactory } from "web-ui/components/internal/Icon";
import Loader from "web-ui/components/internal/Loader";
import Menu from "web-ui/components/internal/Menu";
import MenuItem from "web-ui/components/internal/MenuItem";
import Pagination from "web-ui/components/internal/Pagination";
import Select from "web-ui/components/internal/Select";
import TextInput from "web-ui/components/internal/TextInput";
import useAwaitedValue from "web-ui/components/useAwaitedValue";

import {
  type VoiceListVoice,
  getCombinedVoiceList,
} from "../../api/generalTts";
import type {
  TtsGenerationAge,
  TtsGenerationGender,
} from "../../api/ttsGenerationBackend";
import { getLanguages } from "../../api/ttsGenerationBackend";
import { getDisplayLanguageName } from "../../util/languageUtils";
import HrefLink from "../HrefLink";

import VoiceCard from "./VoiceCard";
import VoiceDrawer from "./VoiceDrawer";
import styles from "./VoiceFinder.module.scss";

const PER_PAGE = 13;

type Filter = (voice: VoiceListVoice) => boolean;

type SortField =
  | "added-at"
  | "added-at-reverse"
  | "language"
  | "language-reverse";

export const formatCharacter = (character: string) =>
  character === "CHARACTER_UNKNOWN" ? "Other" : titleCaseFormatter(character);

export const formatLanguage = (...languages: string[]) =>
  `${languages
    .slice(0, 2)
    .map(lang => `${getDisplayLanguageName(lang)} (${lang})`)
    .join(", ")}${languages.length > 2 ? ` +${languages.length - 2}` : ""}`;

export const formatProvider = (provider: string) => {
  switch (provider) {
    case "AMAZON":
      return "Amazon";
    case "AZURE":
      return "Azure";
    case "ELEVENLABS":
      return "ElevenLabs";
    case "GOOGLE":
      return "Google";
    case "RECORDED":
      return "Recorded";
  }
  return provider;
};

export const titleCaseFormatter = (value: string) =>
  `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;

const CheckboxList = ({
  children,
  double,
}: {
  children: React.ReactNode;
  double?: boolean;
}) => (
  <div className={double ? styles["checkboxes-double"] : styles.checkboxes}>
    {children}
  </div>
);

const CheckboxOptions = ({
  double,
  format = value => value,
  onChange,
  options,
  value,
}: {
  double?: boolean;
  format?: (value: string) => string;
  onChange?: (newValue: string[]) => void;
  options: string[];
  value: string[];
}) => (
  <CheckboxList double={double}>
    {options.map(o => (
      <Checkbox
        checked={value.includes(o)}
        key={o}
        onChange={e => {
          if (e.checked && !value.includes(o)) {
            onChange?.([...value, o]);
          } else if (!e.checked && value.includes(o)) {
            onChange?.(value.filter(v => v !== o));
          }
        }}
      >
        {format(o)}
      </Checkbox>
    ))}
  </CheckboxList>
);

const SortMenu = ({
  onChange,
  value,
}: {
  onChange?: (newValue: SortField) => void;
  value: SortField;
}) => (
  <Select
    leadingVisual={iconFactory("sort")}
    onChange={e => onChange?.(e.value as SortField)}
    value={value}
  >
    <option value="language">Language (A to Z)</option>
    <option value="language-reverse">Language (Z to A)</option>
    <option value="added-at-reverse">Newest</option>
    <option value="added-at">Oldest</option>
  </Select>
);

const VoiceFinder = () => {
  const [accordionKey, rerenderAccordion] = React.useReducer(
    value => value + 1,
    0,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const filterAges = searchParams.getAll("age");
  const filterCharacters = searchParams.getAll("character");
  const filterGenders = searchParams.getAll("gender");
  const filterIsAvailable = searchParams.get("is-available") === "true";
  const filterIsExpressive = searchParams.get("is-expressive") === "true";
  const filterIsMultilingual = searchParams.get("is-multilingual") === "true";
  const filterIsUnavailable = searchParams.get("is-unavailable") === "true";
  const filterLanguages = searchParams.getAll("language");
  const filterMissingTags = searchParams.get("missing-tags") === "true";
  const filterProviders = searchParams.getAll("provider");
  const filterUsedAtDuolingo = searchParams.get("used-at-duolingo") !== "false";
  const page = parseInt(searchParams.get("page") ?? "1", 10);
  const query = searchParams.get("query") ?? "";
  const selectedId = searchParams.get("id") ?? undefined;
  const sortBy = (searchParams.get("sort-by") ?? "language") as SortField;

  const [showDrawer, setShowDrawer] = React.useState(false);

  const data = useAwaitedValue(undefined, () => getCombinedVoiceList(), []);
  const languageData = useAwaitedValue(undefined, () => getLanguages(), []);

  const selected = React.useMemo(
    () => data?.find(voice => voice.id === selectedId),
    [data, selectedId],
  );

  const ageOptions = React.useMemo(
    () =>
      _.uniq(data?.map(voice => voice.age) ?? [])
        .filter((o): o is TtsGenerationAge => o !== undefined)
        .sort((a, b) =>
          titleCaseFormatter(a).localeCompare(titleCaseFormatter(b)),
        ),
    [data],
  );

  const characterOptions = React.useMemo(
    () =>
      _.uniq(
        data?.map(voice => voice.character ?? "CHARACTER_UNKNOWN") ?? [],
      ).sort((a, b) => formatCharacter(a).localeCompare(formatCharacter(b))),
    [data],
  );

  const genderOptions = React.useMemo(
    () =>
      _.uniq(data?.map(voice => voice.gender) ?? [])
        .filter((o): o is TtsGenerationGender => o !== undefined)
        .sort((a, b) =>
          titleCaseFormatter(a).localeCompare(titleCaseFormatter(b)),
        ),
    [data],
  );

  const languageOptions = React.useMemo(() => {
    if (data === undefined || languageData === undefined) {
      return [];
    }
    const matches = languageData
      .filter(
        lang =>
          !filterUsedAtDuolingo ||
          data.some(
            voice =>
              voice.isUsedInLearningApp && voice.languageId === lang.bcp47,
          ),
      )
      .sort((a, b) => Number(b.default_dialect) - Number(a.default_dialect));
    return _.uniq(matches, false, lang => lang.name).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [data, filterUsedAtDuolingo, languageData]);

  const providerOptions = React.useMemo(
    () =>
      _.uniq(data?.map(voice => voice.provider) ?? []).sort((a, b) =>
        formatProvider(a).localeCompare(formatProvider(b)),
      ),
    [data],
  );

  const [filters, filterCount] = React.useMemo(() => {
    const newFilters: Filter[] = [];
    let newCount = 0;
    if (filterAges.length) {
      newFilters.push(
        voice => voice.age !== undefined && filterAges.includes(voice.age),
      );
      newCount += filterAges.length;
    }
    if (filterCharacters.length) {
      newFilters.push(voice =>
        filterCharacters.includes(voice.character ?? "CHARACTER_UNKNOWN"),
      );
      newCount += filterCharacters.length;
    }
    if (filterGenders.length) {
      newFilters.push(
        voice =>
          voice.gender !== undefined && filterGenders.includes(voice.gender),
      );
      newCount += filterGenders.length;
    }
    if (filterIsAvailable) {
      newFilters.push(voice => voice.status !== "UNAVAILABLE");
      newCount++;
    }
    if (filterIsExpressive) {
      newFilters.push(voice => voice.isExpressive);
      newCount++;
    }
    if (filterIsMultilingual) {
      newFilters.push(voice => voice.isMultilingual);
      newCount++;
    }
    if (filterIsUnavailable) {
      newFilters.push(voice => voice.status === "UNAVAILABLE");
      newCount++;
    }
    if (filterLanguages.length) {
      newFilters.push(voice =>
        filterLanguages.some(
          lang =>
            voice.languageId.startsWith(`${lang}-`) ||
            voice.languageStatuses.some(status =>
              status.language.startsWith(`${lang}-`),
            ),
        ),
      );
      newCount += filterLanguages.length;
    }
    if (filterMissingTags) {
      // TODO: how to implement this?
      newFilters.push(() => true);
      newCount++;
    }
    if (filterProviders.length) {
      newFilters.push(voice => filterProviders.includes(voice.provider));
      newCount += filterProviders.length;
    }
    if (filterUsedAtDuolingo) {
      newFilters.push(voice => voice.isUsedInLearningApp);
      newCount++;
    }
    return [newFilters, newCount];
  }, [
    filterAges,
    filterCharacters,
    filterGenders,
    filterIsAvailable,
    filterIsExpressive,
    filterIsMultilingual,
    filterIsUnavailable,
    filterLanguages,
    filterMissingTags,
    filterProviders,
    filterUsedAtDuolingo,
  ]);

  const filteredData = React.useMemo(
    () =>
      data
        ?.filter(
          voice =>
            filters.every(fn => fn(voice)) &&
            (query === "" ||
              voice.alias.toLowerCase().includes(query.toLowerCase())),
        )
        .sort((a, b) => {
          if (sortBy === "language") {
            return formatLanguage(a.languageId).localeCompare(
              formatLanguage(b.languageId),
            );
          } else if (sortBy === "language-reverse") {
            return formatLanguage(b.languageId).localeCompare(
              formatLanguage(a.languageId),
            );
          } else if (sortBy === "added-at") {
            return (a.addedAt?.valueOf() ?? 0) - (b.addedAt?.valueOf() ?? 0);
          } else if (sortBy === "added-at-reverse") {
            return (b.addedAt?.valueOf() ?? 0) - (a.addedAt?.valueOf() ?? 0);
          }
          return 0;
        }),
    [data, filters, query, sortBy],
  );

  const handleArrayFilter = (name: string, newValue: string[]) => {
    setSearchParams(params => {
      params.delete(name);
      params.delete("page");
      newValue.forEach(item => params.append(name, item));
      return params;
    });
  };

  const handleBooleanFilter = (
    name: string,
    newValue: boolean,
    inverse = false,
  ) => {
    setSearchParams(params => {
      if (newValue) {
        if (inverse) {
          params.delete(name);
        } else {
          params.set(name, "true");
        }
      } else {
        if (inverse) {
          params.set(name, "false");
        } else {
          params.delete(name);
        }
      }
      params.delete("page");
      return params;
    });
  };

  const handleStringFilter = (name: string, newValue: string) => {
    setSearchParams(params => {
      if (newValue) {
        params.set(name, newValue);
      } else {
        params.delete(name);
      }
      params.delete("page");
      return params;
    });
  };

  const handleReset = () => {
    setSearchParams(params => {
      params.delete("age");
      params.delete("character");
      params.delete("gender");
      params.delete("is-available");
      params.delete("is-expressive");
      params.delete("is-multilingual");
      params.delete("is-unavailable");
      params.delete("language");
      params.delete("missing-tags");
      params.delete("page");
      params.delete("provider");
      params.delete("query");
      params.set("used-at-duolingo", "true");
      return params;
    });
    // Resets the expanded panels.
    rerenderAccordion();
  };

  React.useEffect(() => {
    setShowDrawer(selected !== undefined);
  }, [selected]);

  return (
    <div className={styles.container}>
      <div className={styles.sidebar}>
        <TextInput
          onChange={e => {
            handleReset();
            handleStringFilter("query", e.value);
          }}
          placeholder="Search by voice name"
          trailingVisual={iconFactory("search")}
          type="search"
          value={query}
        />
        <div className={styles.menu}>
          <span className={styles["menu-name"]}>Collections</span>
          <Menu multiple={true}>
            <MenuItem state="selected">All</MenuItem>
          </Menu>
        </div>
        <div className={styles.menu2}>
          <span className={styles["menu-header"]}>
            <span className={styles["menu-name"]}>Filters</span>
            <span className={styles.count}>{filterCount}</span>
          </span>
          <Accordion
            bordered="in-container"
            inset={false}
            items={[
              {
                content: (
                  <>
                    <Select
                      className={styles.select}
                      onChange={e =>
                        handleArrayFilter("language", e.value ? [e.value] : [])
                      }
                      value={filterLanguages[0] ?? ""}
                    >
                      <option value="">Select language</option>
                      {languageOptions.map(lang => (
                        <option
                          key={lang.bcp47}
                          value={lang.bcp47.split("-")[0]}
                        >
                          {lang.name}
                        </option>
                      ))}
                    </Select>
                    <CheckboxList>
                      <Checkbox
                        checked={filterUsedAtDuolingo}
                        onChange={e =>
                          handleBooleanFilter(
                            "used-at-duolingo",
                            e.checked,
                            true,
                          )
                        }
                      >
                        Duolingo languages
                      </Checkbox>
                    </CheckboxList>
                  </>
                ),
                defaultExpanded: true,
                header: "Language",
              },
              {
                content: (
                  <CheckboxOptions
                    double={true}
                    format={formatCharacter}
                    onChange={newValue =>
                      handleArrayFilter("character", newValue)
                    }
                    options={characterOptions}
                    value={filterCharacters}
                  />
                ),
                header: "Character",
              },
              {
                content: (
                  <CheckboxOptions
                    format={titleCaseFormatter}
                    onChange={newValue => handleArrayFilter("gender", newValue)}
                    options={genderOptions}
                    value={filterGenders}
                  />
                ),
                header: "Gender",
              },
              {
                content: (
                  <CheckboxOptions
                    double={true}
                    format={titleCaseFormatter}
                    onChange={newValue => handleArrayFilter("age", newValue)}
                    options={ageOptions}
                    value={filterAges}
                  />
                ),
                header: "Age",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterIsMultilingual}
                      onChange={e =>
                        handleBooleanFilter("is-multilingual", e.checked)
                      }
                    >
                      Multilingual
                    </Checkbox>
                    <Checkbox
                      checked={filterIsExpressive}
                      onChange={e =>
                        handleBooleanFilter("is-expressive", e.checked)
                      }
                    >
                      Expressive
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Voice features",
              },
              {
                content: (
                  <CheckboxOptions
                    format={formatProvider}
                    onChange={newValue =>
                      handleArrayFilter("provider", newValue)
                    }
                    options={providerOptions}
                    value={filterProviders}
                  />
                ),
                header: "Provider",
              },
              {
                content: <>This section hasn&apos;t been implemented yet</>,
                header: "Quality",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterIsAvailable}
                      onChange={e =>
                        handleBooleanFilter("is-available", e.checked)
                      }
                    >
                      Available
                    </Checkbox>
                    <Checkbox
                      checked={filterIsUnavailable}
                      onChange={e =>
                        handleBooleanFilter("is-unavailable", e.checked)
                      }
                    >
                      Unavailable
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Status",
              },
              {
                content: (
                  <CheckboxList>
                    <Checkbox
                      checked={filterMissingTags}
                      onChange={e =>
                        handleBooleanFilter("missing-tags", e.checked)
                      }
                    >
                      Missing tags
                    </Checkbox>
                  </CheckboxList>
                ),
                header: "Tags",
              },
            ]}
            key={accordionKey}
            textStyle="uppercase"
          />
          <Button
            className={styles.reset}
            onClick={handleReset}
            size="s"
            variant="secondary"
          >
            Reset
          </Button>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.h1}>All Voices</h1>
          <div className={styles["header-total"]}>
            {data === undefined || filteredData === undefined
              ? ""
              : filteredData.length === data.length
                ? `${filteredData.length} ${filteredData.length === 1 ? "voice" : "voices"}`
                : `${filteredData.length} of ${data.length} voices`}
          </div>
          <div className={styles["header-buttons"]}>
            <SortMenu
              onChange={newValue => handleStringFilter("sort-by", newValue)}
              value={sortBy}
            />
            <Button
              leadingVisual={<Icon color="gray-600" name="refresh" />}
              variant="secondary"
            >
              Sync
            </Button>
          </div>
        </div>
        <div className={styles.list}>
          {filteredData === undefined ? (
            <Loader className={styles.loader} />
          ) : filteredData.length === 0 ? (
            <span className={styles.empty}>
              No voices match the filter selection.
            </span>
          ) : (
            filteredData
              .slice((page - 1) * PER_PAGE, page * PER_PAGE)
              .map(voice => (
                <VoiceCard
                  key={voice.alias}
                  onSelect={() => {
                    setSearchParams(params => {
                      params.set("id", voice.id);
                      return params;
                    });
                    setShowDrawer(true);
                  }}
                  state={voice === selected ? "selected" : "enabled"}
                  voice={voice}
                />
              ))
          )}
        </div>
        {filteredData?.length ? (
          <div className={styles.footer}>
            <Pagination
              className={styles.pagination}
              formatLink={newPage => {
                const params = new URLSearchParams(searchParams);
                params.set("page", `${newPage}`);
                return `?${params.toString()}`;
              }}
              linkComponent={HrefLink}
              page={page}
              totalPages={Math.ceil(filteredData.length / PER_PAGE)}
            />
          </div>
        ) : null}
      </div>
      <VoiceDrawer
        className={styles.drawer}
        exampleSentence={
          languageData?.find(lang => lang.bcp47 === selected?.languageId)
            ?.example_sentence
        }
        onRequestClose={() => setShowDrawer(false)}
        open={showDrawer}
        voice={selected}
      />
    </div>
  );
};

export default VoiceFinder;
